import { OfferedProjectType } from "../../../../clients/graphqlTypes.js";

export const matrixCellKey = (
  categoryId: string,
  projectType: OfferedProjectType
) => `${categoryId}|${projectType}`;

export const parseMatrixCellKey = (
  key: string
): { categoryId: string; projectType: OfferedProjectType } => {
  const sepIdx = key.indexOf("|");
  return {
    categoryId: key.slice(0, sepIdx),
    projectType: key.slice(sepIdx + 1) as OfferedProjectType,
  };
};

/** The unsaved edit-session state. `selections` is keyed by `matrixCellKey`. */
export type CatalogueBuffer = {
  declared: ReadonlySet<string>;
  selections: ReadonlySet<string>;
};

export type TriState = "checked" | "indeterminate" | "empty";

export type TaxonomyNode = { id: string; parentId?: string | null };

export type TaxonomyIndex = {
  allIds: string[];
  descendantsOf: (id: string) => string[];
  ancestorsOf: (id: string) => string[];
  isLeaf: (id: string) => boolean;
  has: (id: string) => boolean;
};

export const buildTaxonomyIndex = (
  categories: readonly TaxonomyNode[]
): TaxonomyIndex => {
  const childrenMap = new Map<string, string[]>();
  const parentMap = new Map<string, string | null>();
  for (const c of categories) {
    parentMap.set(c.id, c.parentId ?? null);
    if (!childrenMap.has(c.id)) childrenMap.set(c.id, []);
  }
  for (const c of categories) {
    const parentId = c.parentId ?? null;
    const siblings = parentId ? childrenMap.get(parentId) : undefined;
    if (siblings) siblings.push(c.id);
  }

  const descendantsCache = new Map<string, string[]>();
  const descendantsOf = (id: string): string[] => {
    const cached = descendantsCache.get(id);
    if (cached) return cached;
    const out: string[] = [];
    const stack = [...(childrenMap.get(id) ?? [])];
    let cur = stack.pop();
    while (cur !== undefined) {
      out.push(cur);
      stack.push(...(childrenMap.get(cur) ?? []));
      cur = stack.pop();
    }
    descendantsCache.set(id, out);
    return out;
  };

  const ancestorsOf = (id: string): string[] => {
    const out: string[] = [];
    let cur = parentMap.get(id) ?? null;
    // Stop at a parentId that is not itself a node — a dangling ancestor must
    // not enter a tree path, or MUI renders a filler row for a non-existent id.
    while (cur && parentMap.has(cur)) {
      out.push(cur);
      cur = parentMap.get(cur) ?? null;
    }
    return out;
  };

  return {
    allIds: categories.map(c => c.id),
    descendantsOf,
    ancestorsOf,
    // An id absent from the taxonomy (e.g. a since-deleted category still
    // referenced by persisted org data) is not a leaf — it is not a node at
    // all. Returning `true` here would let stale ids pass leaf-only filters.
    isLeaf: id => childrenMap.get(id)?.length === 0,
    has: id => childrenMap.has(id),
  };
};

/** [id, ...descendants] — used by the grid to expand an activated subtree. */
export const subtreeIds = (index: TaxonomyIndex, id: string): string[] => [
  id,
  ...index.descendantsOf(id),
];

// Non-leaf categories that are "not empty" — declared themselves or with at
// least one declared descendant (i.e. tri-state is not "empty"). The grid
// expands these by default so every declared descendant is visible on entry.
export const expandedNonLeafIds = (
  declared: ReadonlySet<string>,
  index: TaxonomyIndex
): Set<string> => {
  const result = new Set<string>();
  for (const id of index.allIds) {
    if (index.isLeaf(id)) continue;
    if (
      declared.has(id) ||
      index.descendantsOf(id).some(d => declared.has(d))
    ) {
      result.add(id);
    }
  }
  return result;
};

export const activate = (
  buffer: CatalogueBuffer,
  index: TaxonomyIndex,
  id: string,
  projectTypes: readonly OfferedProjectType[]
): CatalogueBuffer => {
  const declared = new Set(buffer.declared);
  const selections = new Set(buffer.selections);
  const subtree = subtreeIds(index, id);
  for (const d of subtree) declared.add(d);
  for (const a of index.ancestorsOf(id)) declared.add(a);
  autoFillLeaves(selections, index, subtree, projectTypes);
  return { declared, selections };
};

export const deactivate = (
  buffer: CatalogueBuffer,
  index: TaxonomyIndex,
  id: string
): CatalogueBuffer => {
  const declared = new Set(buffer.declared);
  declared.delete(id);
  for (const d of index.descendantsOf(id)) declared.delete(d);
  // Selections are intentionally preserved (session-only action memory).
  return { declared, selections: new Set(buffer.selections) };
};

export const toggleAction = (
  buffer: CatalogueBuffer,
  leafId: string,
  projectType: OfferedProjectType
): CatalogueBuffer => {
  const selections = new Set(buffer.selections);
  const key = matrixCellKey(leafId, projectType);
  if (selections.has(key)) selections.delete(key);
  else selections.add(key);
  return { declared: new Set(buffer.declared), selections };
};

export const selectAll = (
  buffer: CatalogueBuffer,
  index: TaxonomyIndex,
  projectTypes: readonly OfferedProjectType[]
): CatalogueBuffer => {
  const declared = new Set(index.allIds);
  const selections = new Set(buffer.selections);
  autoFillLeaves(selections, index, index.allIds, projectTypes);
  return { declared, selections };
};

export const deselectAll = (buffer: CatalogueBuffer): CatalogueBuffer => ({
  declared: new Set<string>(),
  selections: new Set(buffer.selections),
});

// Onboarding cascade from the selected verticals: declare each vertical's full
// subtree (plus its ancestors) and pre-check every leaf in that subtree for the
// chosen project types. Shared source of truth for the specify-matrix prefill
// and the "apply to all" commit, so the two paths write identical offered
// services. Services land on leaves only — the catalogue renders project-type
// cells for leaf categories exclusively, so cells on a non-leaf vertical would
// never surface.
export const prefillBufferFromVerticals = (
  index: TaxonomyIndex,
  verticalIds: readonly string[],
  projectTypes: readonly OfferedProjectType[]
): CatalogueBuffer => {
  const declared = new Set<string>();
  const selections = new Set<string>();
  for (const verticalId of verticalIds) {
    for (const id of subtreeIds(index, verticalId)) {
      declared.add(id);
      if (index.isLeaf(id)) {
        for (const projectType of projectTypes) {
          selections.add(matrixCellKey(id, projectType));
        }
      }
    }
    for (const ancestorId of index.ancestorsOf(verticalId)) {
      declared.add(ancestorId);
    }
  }
  return { declared, selections };
};

export const deriveTriState = (
  buffer: CatalogueBuffer,
  index: TaxonomyIndex,
  id: string
): TriState => {
  const active = buffer.declared.has(id);
  const descendants = index.descendantsOf(id);
  let activeCount = 0;
  for (const d of descendants) {
    if (buffer.declared.has(d)) activeCount++;
  }

  if (!active) {
    return activeCount > 0 ? "indeterminate" : "empty";
  }
  if (index.isLeaf(id)) return "checked";
  return activeCount === descendants.length ? "checked" : "indeterminate";
};

export const deriveSelectAllState = (
  buffer: CatalogueBuffer,
  index: TaxonomyIndex
): TriState => {
  if (index.allIds.length === 0) return "empty";
  let declaredCount = 0;
  for (const id of index.allIds) {
    if (buffer.declared.has(id)) declaredCount++;
  }
  if (declaredCount === 0) return "empty";
  return declaredCount === index.allIds.length ? "checked" : "indeterminate";
};

// Precomputes every node's tri-state once per `(buffer, index)` (memoise on
// that) so each grid row is an O(1) map lookup at render time. Each entry still
// walks its node's descendants, but that cost is paid once per state change
// rather than re-derived per row on every render.
export const deriveAllTriStates = (
  buffer: CatalogueBuffer,
  index: TaxonomyIndex
): Map<string, TriState> => {
  const result = new Map<string, TriState>();
  for (const id of index.allIds) {
    result.set(id, deriveTriState(buffer, index, id));
  }
  return result;
};

// Auto-fills every leaf in `ids` that currently has no action selection with
// all available project types. Non-leaves have no action cells (no-op).
const autoFillLeaves = (
  selections: Set<string>,
  index: TaxonomyIndex,
  ids: readonly string[],
  projectTypes: readonly OfferedProjectType[]
): void => {
  const leavesWithSelection = new Set<string>();
  for (const key of selections) {
    leavesWithSelection.add(parseMatrixCellKey(key).categoryId);
  }
  for (const id of ids) {
    if (!index.isLeaf(id)) continue;
    if (leavesWithSelection.has(id)) continue;
    for (const pt of projectTypes) selections.add(matrixCellKey(id, pt));
  }
};
